(() => {
  'use strict';

  const PRODUCT_GRID_SELECTOR = '#product-grid';
  const PAGINATION_SELECTOR = '.pagination-wrapper';

  let loading = false;
  let finished = false;
  let observer = null;


  /* ============================================================
     GET NEXT PAGE URL
  ============================================================ */

  function getNextPageUrl(root = document) {
    const nextLink =
      root.querySelector(
        `${PAGINATION_SELECTOR} a[aria-label*="Next"], ` +
        `${PAGINATION_SELECTOR} a[aria-label*="next"], ` +
        `${PAGINATION_SELECTOR} .pagination__item--prev + li a`
      );

    if (nextLink?.href) {
      return nextLink.href;
    }


    /*
     * Dawn normally adds a title such as:
     * "Next page"
     */

    const paginationLinks =
      root.querySelectorAll(
        `${PAGINATION_SELECTOR} a`
      );

    for (const link of paginationLinks) {
      const text =
        `${link.getAttribute('aria-label') || ''} ${link.textContent || ''}`
          .trim()
          .toLowerCase();

      if (text.includes('next')) {
        return link.href;
      }
    }

    return null;
  }


  /* ============================================================
     CREATE SENTINEL
  ============================================================ */

  function createSentinel() {
    let sentinel =
      document.querySelector(
        '[data-infinite-scroll-sentinel]'
      );

    if (sentinel) return sentinel;


    sentinel =
      document.createElement('div');

    sentinel.dataset.infiniteScrollSentinel =
      '';

    sentinel.className =
      'infinite-scroll-sentinel';

    sentinel.innerHTML = `
      <div
        class="infinite-scroll-loader"
        data-infinite-scroll-loader
        hidden
      >
        <span class="infinite-scroll-spinner"></span>
        <span>Loading more</span>
      </div>
    `;


    const pagination =
      document.querySelector(
        PAGINATION_SELECTOR
      );


    if (pagination) {
      pagination.parentNode.insertBefore(
        sentinel,
        pagination
      );
    }

    else {
      const grid =
        document.querySelector(
          PRODUCT_GRID_SELECTOR
        );

      grid?.after(sentinel);
    }


    return sentinel;
  }


  /* ============================================================
     LOADER
  ============================================================ */

  function setLoading(value) {
    loading = value;

    const loader =
      document.querySelector(
        '[data-infinite-scroll-loader]'
      );

    if (loader) {
      loader.hidden = !value;
    }
  }


  /* ============================================================
     FETCH NEXT PAGE
  ============================================================ */

  async function loadNextPage() {
    if (loading || finished) return;


    const nextUrl =
      getNextPageUrl();

    if (!nextUrl) {
      finishInfiniteScroll();
      return;
    }


    setLoading(true);


    try {
      const response =
        await fetch(nextUrl, {
          credentials: 'same-origin',

          headers: {
            Accept: 'text/html'
          }
        });


      if (!response.ok) {
        throw new Error(
          `Infinite scroll request failed: ${response.status}`
        );
      }


      const html =
        await response.text();


      const parser =
        new DOMParser();


      const nextDocument =
        parser.parseFromString(
          html,
          'text/html'
        );


      const currentGrid =
        document.querySelector(
          PRODUCT_GRID_SELECTOR
        );


      const nextGrid =
        nextDocument.querySelector(
          PRODUCT_GRID_SELECTOR
        );


      if (!currentGrid || !nextGrid) {
        finishInfiniteScroll();
        return;
      }


      /*
       * Append products from next page.
       */

      const newProducts =
        Array.from(nextGrid.children);


      if (!newProducts.length) {
        finishInfiniteScroll();
        return;
      }


      const fragment =
        document.createDocumentFragment();


      newProducts.forEach((product) => {
        fragment.appendChild(product);
      });


      currentGrid.appendChild(fragment);


      /*
       * Replace current pagination with pagination
       * from newly fetched page.

       * This is important because the next request
       * should now point to page 3, then page 4, etc.
       */

      const currentPagination =
        document.querySelector(
          PAGINATION_SELECTOR
        );


      const nextPagination =
        nextDocument.querySelector(
          PAGINATION_SELECTOR
        );


      if (
        currentPagination &&
        nextPagination
      ) {
        currentPagination.innerHTML =
          nextPagination.innerHTML;
      }

      else if (
        currentPagination &&
        !nextPagination
      ) {
        currentPagination.remove();
      }


      /*
       * Optional URL update.

       * We DON'T change the visible URL by default because
       * scrolling shouldn't suddenly make the browser say
       * ?page=4.

       * Browser Back therefore returns to the collection URL
       * the shopper originally entered.
       */


      /*
       * Tell other custom scripts new product cards exist.
       */

      document.dispatchEvent(
        new CustomEvent(
          'infinite-scroll:loaded',
          {
            detail: {
              url: nextUrl,
              count: newProducts.length
            }
          }
        )
      );


      /*
       * If there's no next page anymore, stop.
       */

      if (!getNextPageUrl()) {
        finishInfiniteScroll();
      }

    }

    catch (error) {
      console.error(
        'Infinite scroll:',
        error
      );


      /*
       * Don't endlessly retry a broken page.
       */

      finished = true;

      showLoadMoreFallback();
    }

    finally {
      setLoading(false);
    }
  }


  /* ============================================================
     FINISH
  ============================================================ */

  function finishInfiniteScroll() {
    finished = true;

    if (observer) {
      observer.disconnect();
    }


    const sentinel =
      document.querySelector(
        '[data-infinite-scroll-sentinel]'
      );

    if (sentinel) {
      sentinel.remove();
    }
  }


  /* ============================================================
     FALLBACK BUTTON
  ============================================================ */

  function showLoadMoreFallback() {
    const sentinel =
      document.querySelector(
        '[data-infinite-scroll-sentinel]'
      );

    if (!sentinel) return;


    const nextUrl =
      getNextPageUrl();


    if (!nextUrl) return;


    sentinel.innerHTML = `
      <a
        href="${nextUrl}"
        class="infinite-scroll-fallback"
      >
        Load more products
      </a>
    `;
  }


  /* ============================================================
     INTERSECTION OBSERVER
  ============================================================ */

  function setupObserver() {
    if (finished) return;


    const sentinel =
      createSentinel();


    if (!sentinel) return;


    observer =
      new IntersectionObserver(
        (entries) => {

          entries.forEach((entry) => {

            if (
              entry.isIntersecting &&
              !loading &&
              !finished
            ) {
              loadNextPage();
            }

          });

        },

        {
          /*
           * Start loading BEFORE user reaches bottom.
           * Gives next products time to appear smoothly.
           */

          root: null,

          rootMargin:
            '800px 0px 800px 0px',

          threshold: 0
        }
      );


    observer.observe(sentinel);
  }


  /* ============================================================
     RESET AFTER SHOPIFY FILTER/SORT
  ============================================================ */

  function resetInfiniteScroll() {
    if (observer) {
      observer.disconnect();
    }


    loading = false;
    finished = false;


    document
      .querySelector(
        '[data-infinite-scroll-sentinel]'
      )
      ?.remove();


    window.setTimeout(() => {

      if (
        document.querySelector(
          PRODUCT_GRID_SELECTOR
        )
      ) {
        setupObserver();
      }

    }, 100);
  }


  /*
   * Dawn's collection filters can replace the grid through Ajax.
   *
   * MutationObserver makes infinite scroll rebuild itself when
   * Shopify replaces product-grid markup.
   */

  let previousGrid =
    document.querySelector(
      PRODUCT_GRID_SELECTOR
    );


  const pageObserver =
    new MutationObserver(() => {

      const currentGrid =
        document.querySelector(
          PRODUCT_GRID_SELECTOR
        );


      if (
        currentGrid &&
        currentGrid !== previousGrid
      ) {
        previousGrid =
          currentGrid;

        resetInfiniteScroll();
      }

    });


  pageObserver.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );


  /* ============================================================
     INITIALIZE
  ============================================================ */

  function init() {
    const grid =
      document.querySelector(
        PRODUCT_GRID_SELECTOR
      );


    if (!grid) return;


    /*
     * No pagination = only one page.
     */

    if (!getNextPageUrl()) {
      return;
    }


    setupObserver();
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  }

  else {
    init();
  }

})();